import { orderToReplaceOrder } from '@tradingblock/api';
import {
  TradingblockOrder,
  DebitOrCredit,
  Order,
  OrderLeg,
  OrderLegDTO,
  OrderType,
  UserProfileLevel,
  OrderStrategyType,
  AssetType,
} from '@tradingblock/types';
import _ from 'lodash';
import React, { useState, useCallback, useMemo, useEffect } from 'react';
import { OrderActions } from '../../../../data/global/actions';
import { useStateSelector } from '../../../../data/global/dataSelectors';
import { useDispatcher } from '../../../../data/global/hooks';
import { IsWarningSymbol } from '../../../../data/global/selectors/symbolSelector';
import { useDashboardSettings } from '../../../../hooks/useDashboardSettings';
import { useSounds } from '../../../../hooks/useSounds';
import { useOrderSettings } from '../../hooks/useOrderSettings';
import { useOrderTotal } from '../../hooks/useOrderTotal';
import { useOrderPrice } from '../../hooks/useOrderPrice';
import { PreviewResult } from './PreviewPopup';
import {
  buildConfirmDebitCreditContextForPreview,
  resolveConfirmOrderDebitCredit,
} from './resolveConfirmOrderDebitCredit';
import { resolveStrategyTypeForPlacement } from './resolveStrategyTypeForPlacement';
import { legToDto } from '../../OrderUtilities';
import { OrderState, useOrderBlockData } from '../../state/OrderState';
import { OrderSelectors } from '../../state/OrderSelector';
import { useOrderActions } from '../../state/useOrderActions';

export type PreviewActions = 'execute' | 'replace' | 'error';

export const useOrderPreview = () => {
  const [isPreview, setIsPreview] = useState(false);

  const { successControls, errorControls, successAudio, errorAudio } = useSounds();
  const { disableSounds } = useOrderSettings();
  const { orderPlaced } = useOrderActions();
  const { muteAllSounds } = useDashboardSettings();
  const togglePreview = useCallback(() => setIsPreview(!isPreview), [isPreview]);

  const previewClosed = useCallback(
    (result: PreviewResult) => {
      setIsPreview(false);
      if (disableSounds !== true && result.action === 'order-placed') {
        !muteAllSounds &&
          (() => {
            const play = successControls.play();
            if (play !== undefined) {
              play.catch(() => {
                // Do nothing
                //TODO: Determine if we want to warn the user that the sound failed to play
              });
            }
          })();
        orderPlaced();
        // Note: Toast notifications are now handled in notificationMiddleware
        // when the order is actually placed (via WebSocket updates)
      } else if (disableSounds !== true && result.action === 'error') {
        !muteAllSounds &&
          (() => {
            const play = errorControls.play();
            if (play !== undefined) {
              play.catch(() => {
                // Do nothing
                //TODO: Determine if we want to warn the user that the sound failed to play
              });
            }
          })();
        // Note: Toast notifications are now handled in notificationMiddleware
        // when order errors occur
      } else {
        //do nothing if popup is just closed
      }
    },
    [setIsPreview, successControls, errorControls, disableSounds],
  );

  return useMemo(
    () => ({
      isPreview,
      togglePreview,
      previewClosed,
      successAudio,
      errorAudio,
      /** Used by OrderFooter, ActivityOrderContent (`audio.successAudio` / `audio.errorAudio`). */
      audio: { successAudio, errorAudio },
      successControls,
      errorControls,
    }),
    [isPreview, togglePreview, previewClosed, successAudio, errorAudio, successControls, errorControls],
  );
};

type OptionLegLike = Pick<OrderLegDTO, 'Expiration' | 'Strike' | 'OptionType' | 'Action'>;

const getOptionLegKey = (l: OptionLegLike): string => {
  const expiration = l.Expiration;
  let exp: string | number | undefined;
  if (expiration && expiration.date instanceof Date) {
    exp = expiration.date.getTime();
  } else if (expiration) {
    const withDate = expiration as { date?: unknown };
    exp = withDate.date as string | number | undefined;
  } else {
    exp = undefined;
  }
  const strikePart = l.Strike != null ? String(l.Strike) : '';
  const expPart = exp != null ? String(exp) : '';
  const optPart = l.OptionType != null ? String(l.OptionType) : '';
  const actPart = l.Action != null ? String(l.Action) : '';
  return strikePart + '-' + expPart + '-' + optPart + '-' + actPart;
};

export const getOrderPreviewLegs = (order: TradingblockOrder | Order<OrderLeg>): OrderLegDTO[] => {
  const hasMultipleLegs = order.Legs && order.Legs.length > 1;

  const filtered = (order.Legs != null ? order.Legs : [])
    .filter(l => {
      if (_.isNil(l.Symbol)) {
        return false;
      }
      if (hasMultipleLegs) {
        return Boolean(l.Symbol && l.SpreadRatio);
      }
      return Boolean(l.Symbol);
    })
    .map(legToDto);

  // Deduplicate by option identity - prevents accumulation when legs are duplicated in state
  const seen = new Set<string>();
  return filtered.filter(l => {
    const key = getOptionLegKey(l);
    if (seen.has(key)) {
      return false;
    }
    seen.add(key);
    return true;
  });
};

const adjustOrderQuantitiesAndRatios = (order: Order<OrderLeg>) => {
  if (!order) {
    return [0, []];
  }
  // function to find greatest common divisor
  const gcd = (a: number, b: number): number => {
    return b === 0 ? a : gcd(b, a % b);
  };

  // function to find highest common factor
  const findHCF = (arr: number[]): number => {
    return arr.reduce((a, b) => gcd(a, b));
  };

  const hcf = findHCF(order.Legs.map(l => l.SpreadRatio || 1));

  const adjustedOverallQuantity = order.Quantity * hcf;

  const adjustedRatios = order.Legs.map(l => {
    const ratio = l.SpreadRatio || 1;
    const adjustedRatio = ratio / hcf;
    return {
      ...l,
      SpreadRatio: adjustedRatio,
      Quantity: adjustedOverallQuantity * adjustedRatio,
    };
  });

  return [adjustedOverallQuantity, adjustedRatios];
};

function finalizeOrder<T extends Order>(
  order: T,
  accountId?: number,
  debitOrCredit?: DebitOrCredit,
  subaccountId?: number,
): T {
  // determine the strategy of the order
  const isCustom = order.StrategyType === 'Undefined';
  // determine if all legs are option legs
  const allOptionLegs = order.Legs.every(l => l.AssetType === AssetType.Option);
  if (isCustom && allOptionLegs && order && order.Legs && order.Legs.length > 1) {
    const [adjustedOverallQuantity, adjustedRatios] = adjustOrderQuantitiesAndRatios(order);

    return {
      ...order,
      AccountId: accountId,
      DebitCredit: debitOrCredit,
      SubaccountId: subaccountId,
      Quantity: adjustedOverallQuantity,
      Legs: adjustedRatios,
    };
  }

  return {
    ...order,
    AccountId: accountId,
    DebitCredit: debitOrCredit,
    SubaccountId: subaccountId,
  };
}

export { resolveStrategyTypeForPlacement } from './resolveStrategyTypeForPlacement';

export const useOrderPreviewOrder = (action: PreviewActions) => {
  const orderFromLocal = useOrderBlockData(OrderSelectors.order);
  const orderData = useOrderBlockData((s: OrderState) => s);

  // Preview uses block-local order from OrderSelectors only. Option-chain embedded merge depended on
  // modules that are not present in this package; that merge also caused wrong cancel/replace previews.
  const order = useMemo(() => orderFromLocal, [orderFromLocal]);

  const [initialPrice, setInitialPrice] = useState<number>();
  const [replacePrice, setReplacePrice] = useState<number>();
  const [initialStopPrice, setInitialStopPrice] = useState<number>();
  const [replaceStopPrice, setReplaceStopPrice] = useState<number>();
  const [orderId, setOrderId] = useState<number>();
  const { setStopPrice, setPrice, setDebitCredit, setSubaccountId, setOrder: setOrderCallback } = useOrderActions();
  const accountId = useStateSelector(state => state.account.accountId);
  const profile = useStateSelector(state => state.account.profile);
  const userLevel = profile && profile.current && profile.current.level;
  const isAboveAccountLevel = userLevel && userLevel !== UserProfileLevel.Account;
  const subAccounts = useStateSelector(state => state.account.subaccounts.subaccounts);

  useEffect(() => {
    const orderPrice = order ? order.Price : undefined;
    const currId = order ? order.OrderId : undefined;
    if (order && (initialPrice === undefined || orderId !== currId) && orderPrice) {
      setInitialPrice(orderPrice);
      setOrderId(currId);
      if (order && order.OrderType === OrderType.Stop_Limit && initialStopPrice === undefined && order.Stop) {
        setInitialStopPrice(order.Stop);
      }
    }
  }, [order, initialPrice, orderId, initialStopPrice]);

  useEffect(() => {
    const subaccountId = order && order.SubaccountId;
    const isSubaccountDeleted = subaccountId && subAccounts && !subAccounts.find(sa => sa.Id === subaccountId);
    const hasNoSubAccounts = !subAccounts && subaccountId;

    // If the user has deleted the subaccount associated with the order,
    // or if there are no subaccounts but the order has a subaccountId, we need to remove the subaccount designation from the order object.
    if (subaccountId && (isSubaccountDeleted || hasNoSubAccounts)) {
      setSubaccountId(undefined);
    }
  }, [order, subAccounts]);

  // const orderlegs = useOrderData(orderLegsSelector);
  const legs = useMemo(() => {
    if (order) {
      return getOrderPreviewLegs(order);
    }
    return [];
  }, [order]);

  const totalResults = useOrderTotal();
  const { mid, openingNetDebitMinusCredit } = useOrderPrice();
  const orderDataStrategy = orderData != null ? orderData.strategy : undefined;
  const orderDataLegs = orderData != null ? orderData.legs : undefined;
  const validationMatch =
    orderData != null && orderData.validation != null ? orderData.validation.match : undefined;
  const confirmDebitCreditContext = useMemo(
    () =>
      buildConfirmDebitCreditContextForPreview({
        mid,
        openingNetDebitMinusCredit,
        strategy: orderDataStrategy,
        legs: orderDataLegs,
        validationMatch,
        order,
        blockDebitCreditFallback: totalResults.debitCredit,
      }),
    [
      mid,
      openingNetDebitMinusCredit,
      orderDataStrategy,
      orderDataLegs,
      validationMatch,
      order,
      totalResults.debitCredit,
    ],
  );
  const isWarningSymbol = useStateSelector(s => IsWarningSymbol(s));

  const isTradingblockOrder = useMemo(() => {
    if (order) {
      return (order as TradingblockOrder).kind === 'order';
    }
    return false;
  }, [order]);

  const finalOrder = useMemo(() => {
    if (_.isNil(order)) {
      return undefined;
    }
    const debitOrCredit = resolveConfirmOrderDebitCredit(order, confirmDebitCreditContext);
    const strategyTypeForPlacement = resolveStrategyTypeForPlacement(order);
    // if the order strategy type contains vertical, calendar, butterfly, or condor we need to set the strategy type accordingly
    // currently the backend does not differentiate between call and put verticals/calendars/butterflies/condors so we need to do it here
    return finalizeOrder(
      {
        ...order,
        BypassWarnings: isWarningSymbol,
        StrategyType:
          strategyTypeForPlacement && _.includes(strategyTypeForPlacement, 'Vertical')
            ? OrderStrategyType.Vertical
            : strategyTypeForPlacement && _.includes(strategyTypeForPlacement, 'Calendar')
              ? OrderStrategyType.Calendar
              : strategyTypeForPlacement && strategyTypeForPlacement === OrderStrategyType.Call
                ? OrderStrategyType.Single
                : strategyTypeForPlacement && strategyTypeForPlacement === OrderStrategyType.Put
                  ? OrderStrategyType.Single
                  : strategyTypeForPlacement && _.includes(strategyTypeForPlacement, 'IronButterfly')
                    ? OrderStrategyType.IronButterfly
                    : strategyTypeForPlacement && _.includes(strategyTypeForPlacement, 'IronCondor')
                      ? OrderStrategyType.IronCondor
                      : strategyTypeForPlacement && _.includes(strategyTypeForPlacement, 'Butterfly')
                        ? OrderStrategyType.Butterfly
                        : strategyTypeForPlacement && _.includes(strategyTypeForPlacement, 'Condor')
                          ? OrderStrategyType.Condor
                          : strategyTypeForPlacement && _.includes(strategyTypeForPlacement, 'Collar')
                            ? OrderStrategyType.Collar
                            : strategyTypeForPlacement,
      },
      order.AccountId || accountId,
      debitOrCredit,
      order.SubaccountId,
    );
  }, [order, confirmDebitCreditContext, accountId, isWarningSymbol]);

  const changeSubaccountId = useCallback(
    (id?: number) => {
      if (order === undefined) {
        return;
      }
      setSubaccountId(id);
    },
    [setSubaccountId, order],
  );

  const changeLimitPrice = useCallback(
    (price: number | null, isNegativePrice?: boolean) => {
      if (order === undefined) {
        return;
      }
      if (price !== null) {
        setPrice(price, false, isNegativePrice, true);
      }
    },
    [order],
  );

  const changeStopPrice = useCallback(
    (price: number | null) => {
      if (order === undefined) {
        return;
      }
      if (price !== null) {
        setStopPrice(price);
      }
    },
    [order],
  );

  const changeDebitCredit = useCallback(
    (val: DebitOrCredit | undefined) => {
      if (order === undefined) {
        return;
      }
      setDebitCredit(val);
    },
    [order],
  );

  const changeReplacePrice = useCallback(
    (price: number | null) => {
      if (order === undefined) {
        return;
      }
      if (price !== null) {
        setReplacePrice(price);
      }
    },
    [order],
  );
  const changeReplaceStopPrice = useCallback(
    (price: number | null) => {
      if (order === undefined) {
        return;
      }
      if (price !== null) {
        setReplaceStopPrice(price);
      }
    },
    [order],
  );
  const setOrder = useCallback(
    (o: Order) => {
      if (setOrderCallback) {
        setOrderCallback(o);
      }
    },
    [setOrderCallback],
  );

  const { dispatch } = useDispatcher();
  const onSave = useCallback(() => {
    if (finalOrder) {
      if (isAboveAccountLevel) {
        finalOrder.BypassWarnings = true;
      }
      if (finalOrder.Legs && finalOrder.Legs.length === 1) {
        finalOrder.Legs[0].SpreadRatio = undefined;
      }
      if (action === 'execute' || action === undefined) {
        dispatch(OrderActions.requestPlaceOrder(finalOrder));
      } else if (action === 'replace') {
        dispatch(OrderActions.requestChange(orderToReplaceOrder(finalOrder)));
      }
      if (finalOrder.Price) {
        setInitialPrice(finalOrder.Price);
      }
    }
  }, [finalOrder, action, isAboveAccountLevel, dispatch, setInitialPrice]);

  return {
    order,
    /** Pass to {@link resolveConfirmOrderDebitCredit} with the same `order` shown in the modal (e.g. parent override). */
    confirmDebitCreditContext,
    legs,
    action,
    initialPrice,
    replacePrice,
    initialStopPrice,
    replaceStopPrice,
    isWarningSymbol,
    isTradingblockOrder,
    setOrder,
    changeSubaccountId,
    changeDebitCredit,
    changeLimitPrice,
    changeStopPrice,
    changeReplacePrice,
    changeReplaceStopPrice,
    onSave,
  };
};
